import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import InputAdornment from '@mui/material/InputAdornment';
import { saveCustomerSession } from '../api';
import { requestOtp, toApiError, verifyOtp } from '../features/hangouts/api';

// Customer sign-in: mobile number → 6-digit SMS code. First-time customers also pick the display
// name other people in their hangouts will see, and confirm they're of legal drinking age.
const CustomerLoginForm: React.FC<{ onSignedIn: () => void }> = ({ onSignedIn }) => {
  const [step, setStep] = useState<'mobile' | 'code'>('mobile');
  const [mobile, setMobile] = useState('');
  const [code, setCode] = useState('');
  const [isNew, setIsNew] = useState(false);
  const [name, setName] = useState('');
  const [ageOk, setAgeOk] = useState(false);
  const [devCode, setDevCode] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = window.setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => window.clearTimeout(t);
  }, [resendIn]);

  const sendCode = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const r = await requestOtp(mobile);
      setIsNew(r.isNewCustomer);
      setDevCode(r.devCode ?? null);
      setResendIn(r.resendAfterSeconds);
      setStep('code');
    } catch (err) {
      setError(toApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const r = await verifyOtp({ mobile, code, displayName: isNew ? name : undefined, ageConfirmed: isNew ? ageOk : true });
      saveCustomerSession(r.accessToken, { displayName: r.customer.displayName, initials: r.customer.initials });
      onSignedIn();
    } catch (err) {
      setError(toApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  if (step === 'mobile') {
    return (
      <Box component="form" onSubmit={sendCode} display="flex" flexDirection="column" gap={2}>
        <Typography color="text.secondary" fontSize={14}>Sign in with your mobile number to create and join hangouts. You don't need an account to browse. No password needed.</Typography>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField
          label="Mobile number"
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          required
          value={mobile}
          onChange={(e) => setMobile(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start">+91</InputAdornment> }}
        />
        <Button type="submit" variant="contained" color="primary" size="large" disabled={busy || mobile.replace(/\D/g, '').length < 10}>
          {busy ? 'Sending code…' : 'Send code'}
        </Button>
        <Typography color="text.secondary" fontSize={12}>
          By continuing you agree to our <Link to="/terms" style={{ color: 'inherit' }}>Terms of Use</Link> and <Link to="/privacy" style={{ color: 'inherit' }}>Privacy Policy</Link>.
        </Typography>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={verify} display="flex" flexDirection="column" gap={2}>
      <Typography color="text.secondary" fontSize={14}>
        Enter the 6-digit code sent to +91 {mobile.replace(/\D/g, '').slice(-10)}.{' '}
        <Box component="span" sx={{ color: 'primary.main', cursor: 'pointer' }} onClick={() => { setStep('mobile'); setCode(''); setError(null); }}>Change number</Box>
      </Typography>
      {devCode && <Alert severity="info">Development mode: no SMS was sent. Your code is <b>{devCode}</b>.</Alert>}
      {error && <Alert severity="error">{error}</Alert>}
      <TextField
        label="6-digit code"
        required
        autoFocus
        inputMode="numeric"
        autoComplete="one-time-code"
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
        inputProps={{ maxLength: 6, style: { letterSpacing: 8, fontSize: 20 } }}
      />
      {isNew && (
        <>
          <TextField label="Your name" required value={name} onChange={(e) => setName(e.target.value)} inputProps={{ maxLength: 40 }}
            helperText="Shown to people in your hangouts. Your number stays private." />
          <FormControlLabel
            control={<Checkbox checked={ageOk} onChange={(e) => setAgeOk(e.target.checked)} />}
            label={<Typography fontSize={13}>I'm of legal drinking age in my state and agree to follow venue entry rules.</Typography>}
          />
        </>
      )}
      <Button type="submit" variant="contained" color="primary" size="large" disabled={busy || code.length !== 6 || (isNew && (!name.trim() || !ageOk))}>
        {busy ? 'Checking…' : isNew ? 'Create account' : 'Sign in'}
      </Button>
      <Button type="button" disabled={busy || resendIn > 0} onClick={() => sendCode()} sx={{ color: 'text.secondary' }}>
        {resendIn > 0 ? `Resend code in ${resendIn}s` : 'Resend code'}
      </Button>
    </Box>
  );
};

export default CustomerLoginForm;
